import { Button, Checkbox, FormControlLabel, Grid, TextField, Typography, Box } from "@mui/material";
import React from "react";
import { useStyles } from "./MQTTSettingEditor.styles";
import { Formik } from "formik";
import {
    IMQTTBrokerSettingWithoutId,
    IMQTTBrokerSettings,
} from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";

export type MQTTSettingEditorProps = {
    currentSetting?: IMQTTBrokerSettings;
    testMosquittoBroker: (setting: IMQTTBrokerSettingWithoutId) => void;
    handleSave: (values: IMQTTBrokerSettingWithoutId) => void;
    loading?: boolean;
};

export const MQTTSettingEditor: React.FC<MQTTSettingEditorProps> = ({
    currentSetting,
    testMosquittoBroker,
    handleSave,
    loading = false,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const mqttSettingSchema = Yup.object().shape({
        name: Yup.string()
            .max(20, t("mqtt_settings.validation.field_too_long"))
            .required(t("mqtt_settings.validation.required")),
        host: Yup.string().required(t("mqtt_settings.validation.required")),
        allow_anonymous: Yup.boolean().required(t("mqtt_settings.validation.required")),
    });

    const validate = (values: Omit<IMQTTBrokerSettings, "id">) => {
        if (values.allow_anonymous) {
            return {};
        }

        const errors: Partial<Omit<IMQTTBrokerSettings, "id">> = {};
        if (!values.user) {
            errors.user = t("mqtt_settings.validation.required_if_not_anonymous");
        }
        if (!values.password) {
            errors.password = t("mqtt_settings.validation.required_if_not_anonymous");
        }

        return errors;
    };

    const initialValues: Omit<IMQTTBrokerSettings, "id"> = currentSetting
        ? {
              name: currentSetting.name || "",
              allow_anonymous: currentSetting.allow_anonymous || false,
              host: currentSetting.host || "",
              user: currentSetting.user || "",
              password: currentSetting.password || "",
          }
        : {
              name: "",
              allow_anonymous: false,
              host: "",
              user: "",
              password: "",
          };

    return (
        <Box className={classes.container}>
            <Typography className={classes.title}>{t("mqtt_settings.title")}</Typography>

            <Formik
                validate={validate}
                initialValues={initialValues}
                validationSchema={mqttSettingSchema}
                onSubmit={handleSave}
                enableReinitialize={true}
            >
                {({ values, isSubmitting, errors, touched, setFieldValue, submitForm }) => {
                    return (
                        <Box className={classes.formContainer}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="text"
                                        variant="outlined"
                                        label={t("mqtt_settings.setting_name")}
                                        value={values.name}
                                        onChange={(e) => setFieldValue("name", e.target.value)}
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={touched.name && errors.name}
                                        disabled={loading}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="text"
                                        variant="outlined"
                                        label={t("mqtt_settings.host")}
                                        value={values.host}
                                        onChange={(e) => setFieldValue("host", e.target.value)}
                                        error={touched.host && Boolean(errors.host)}
                                        helperText={touched.host && errors.host}
                                        disabled={loading}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                className={classes.checkbox}
                                                checked={values.allow_anonymous}
                                                onChange={(e) => setFieldValue("allow_anonymous", e.target.checked)}
                                                disabled={loading}
                                            />
                                        }
                                        label={t("mqtt_settings.allow_anonymous_connection")}
                                    />
                                </Grid>

                                {!values.allow_anonymous && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                type="text"
                                                variant="outlined"
                                                label={t("mqtt_settings.username")}
                                                value={values.user}
                                                onChange={(e) => setFieldValue("user", e.target.value)}
                                                error={touched.user && Boolean(errors.user)}
                                                helperText={touched.user && errors.user}
                                                disabled={loading}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                type="password"
                                                variant="outlined"
                                                label={t("mqtt_settings.password")}
                                                value={values.password}
                                                onChange={(e) => setFieldValue("password", e.target.value)}
                                                error={touched.password && Boolean(errors.password)}
                                                helperText={touched.password && errors.password}
                                                disabled={loading}
                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>

                            <Box className={classes.buttonContainer}>
                                <Button
                                    variant="outlined"
                                    onClick={() => testMosquittoBroker(values)}
                                    disabled={loading || isSubmitting}
                                    className={classes.testButton}
                                >
                                    {t("mqtt_settings.test_connection")}
                                </Button>

                                <Button
                                    variant="contained"
                                    onClick={submitForm}
                                    disabled={loading || isSubmitting}
                                    className={classes.saveButton}
                                >
                                    {currentSetting
                                        ? t("mqtt_settings.update_setting")
                                        : t("mqtt_settings.save_setting")}
                                </Button>
                            </Box>
                        </Box>
                    );
                }}
            </Formik>
        </Box>
    );
};
